import { CoolHeading } from "../CoolHeading";
import { Button, ScrollShadow } from "@heroui/react";
import { Game, PlayerScore } from "../../types";
import { Fragment } from "react/jsx-runtime";
import approximateScore from "../../utils/approximate-scores";
import normalizedMachineNameSort from "../../utils/normalized-machine-name-sort";
import { useMatchFilters } from "../../hooks/useMatchFilters";
import { useActiveMatch } from "../../hooks/useActiveMatch";
import { useTeamStats } from "../../hooks/useTeamStats";
import { Loading } from "../Loading";
import { useEffect, useState } from "react";
import { groupBy } from "../../utils/group-by";
import { NoDataAvailable } from "../NoDataAvailable";
import { ROUNDS } from "../match/RoundFilter";

function truncateNameIfNeeded(name: string) {
  if (name.length > 27) {
    const idx = Math.min(
      ...[":", "("].map((c) => name.indexOf(c)).filter((i) => i !== -1)
    );
    if (idx !== Infinity && idx > 0) {
      return name.slice(0, idx);
    }
    return name.slice(0, 27) + "...";
  }
  return name;
}

export function Records() {
  const {
    selectedPlayers,
    selectedMachines,
    setSelectedMachines,
    setPickOrRespond,
    setSinglesOrDoubles,
  } = useMatchFilters();
  const { data, loading } = useTeamStats();

  const { match } = useActiveMatch();

  const [tableData, setTableData] = useState(convertTableData());

  useEffect(() => {
    setTableData(convertTableData());
  }, [loading, selectedMachines, selectedPlayers, data]);

  if (loading || !data) {
    return <Loading />;
  }

  function convertTableData() {
    if (!data || !match) return [];

    const machines = normalizedMachineNameSort(data.machines).filter(
      (m) =>
        selectedMachines.length === 0 ||
        selectedMachines.includes(m.machineGroupId)
    );
    const matches = data.matches;

    const allPlayersSelected = data.roster.length === selectedPlayers.length;

    const tableData = machines
      .map((machine) => {
        const machineMatches = matches
          .filter((m) => m.machineGroupId === machine.machineGroupId)
          .filter((m) => {
            return m.playerScores.some(
              (p) => match.opponent__v2.id === p.teamId && p.activeOnCurrentTeam
            );
          })
          .filter((m) => {
            return m.playerScores.some((p) =>
              selectedPlayers.includes(p.playerId)
            );
          })
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );

        const groupedMatches = groupBy(
          machineMatches,
          (m) => m.simulatedHomeRoundNumber
        );

        const roundDataForMachine = ROUNDS.map((round) => {
          const roundMatches = groupedMatches[round] || [];
          const wins = roundMatches.filter((m) => m.win).length;
          const losses = roundMatches.length - wins;
          const winPercentage = wins / roundMatches.length;

          return {
            round,
            wins,
            losses,
            winPercentage,
            roundMatches,
          };
        });

        return {
          machine,
          roundDataForMachine,
        };
      })
      .filter(({ roundDataForMachine }) => {
        return (
          allPlayersSelected ||
          roundDataForMachine.some((rd) => rd.wins > 0 || rd.losses > 0)
        );
      });

    return tableData;
  }

  function setParamsBasedOnRound(round: string) {
    if (round === "1") {
      setPickOrRespond(["respond"]);
      setSinglesOrDoubles(["doubles"]);
    } else if (round === "2") {
      setPickOrRespond(["pick"]);
      setSinglesOrDoubles(["singles"]);
    } else if (round === "3") {
      setPickOrRespond(["respond"]);
      setSinglesOrDoubles(["singles"]);
    } else if (round === "4") {
      setPickOrRespond(["pick"]);
      setSinglesOrDoubles(["doubles"]);
    } else if (round === "T") {
      console.log("stubbed, need to figure this out");
      setPickOrRespond(null);
      setSinglesOrDoubles(null);
    }
  }

  return (
    <div className="w-full lg:w-[600px] xl:w-1/2">
      {tableData.length === 0 ? (
        <NoDataAvailable pluralNoun="records" />
      ) : (
        <>
          <div className="grid grid-cols-[1.4fr,1fr,1fr,1fr,1fr,1fr] items-center gap-2">
            <div className="" />
            {ROUNDS.map((round) => (
              <Button
                key={round}
                onPress={() => {
                  setParamsBasedOnRound(round.toString());
                }}
                className="mb-2 min-w-2 flex-1 rounded-full bg-gray-200 bg-inherit text-sm text-yellow"
              >
                <CoolHeading
                  variant="subtle"
                  className="mb-2 flex-1 text-center text-sm text-yellow md:text-lg"
                >
                  {round}
                </CoolHeading>
              </Button>
            ))}
          </div>
          <ScrollShadow className="grid max-h-[40rem] grid-cols-[1.4fr,1fr,1fr,1fr,1fr,1fr] items-center gap-2 overflow-y-scroll 2xl:max-h-[50vh]">
            {tableData.map(
              ({
                machine: { machineGroupId, machineGroupName },
                roundDataForMachine,
              }) => (
                <Fragment key={machineGroupId}>
                  <Button
                    key={machineGroupId}
                    onPress={() => {
                      setSelectedMachines([machineGroupId]);
                    }}
                    className="min-w-10 overflow-visible rounded-full bg-inherit px-2"
                  >
                    <p className="flex-1 text-wrap text-right text-[0.5rem] leading-[0.8rem] md:text-sm">
                      {truncateNameIfNeeded(machineGroupName)}
                    </p>
                  </Button>
                  {roundDataForMachine.map(
                    ({ round, wins, losses, winPercentage }) => {
                      return (
                        <Button
                          key={round}
                          className={
                            "h-16 min-w-2 flex-1 rounded-xl text-center text-[0.7rem] md:text-xs"
                          }
                          style={{
                            backgroundColor:
                              wins || losses
                                ? colorPercentile(winPercentage)
                                : "inherit",
                          }}
                          onPress={() => {
                            setSelectedMachines([machineGroupId]);
                            setParamsBasedOnRound(round.toString());
                          }}
                        >
                          {wins} - {losses}
                        </Button>
                      );
                    }
                  )}
                </Fragment>
              )
            )}
          </ScrollShadow>
        </>
      )}
    </div>
  );
}

function colorPercentile(pct: number) {
  if (pct < 0.5) {
    const ratio = pct / 0.5;
    return `color-mix(in srgb, hsl(var(--heroui-pink)) ${100 - ratio * 100}%, transparent ${ratio * 100}%)`;
  } else {
    const ratio = (pct - 0.5) / 0.5;
    return `color-mix(in srgb, transparent ${100 - ratio * 100}%, hsl(var(--heroui-green)) ${ratio * 100}%)`;
  }
}

export function MatchCard({
  match,
  teamId,
  showName,
}: {
  match: Game;
  teamId: number;
  showName?: boolean;
}) {
  return (
    <>
      {showName && (
        <p className="mb-4 max-w-32 text-wrap text-center text-yellow">
          {match.machineName}
        </p>
      )}
      <div className="flex w-full flex-col items-center rounded-xl bg-gray-200 p-2 dark:bg-gray-700">
        <span className="text-xs">
          {new Date(match.date).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
        <span className="mb-2 text-xs">{match.venue}</span>
        <div className="flex w-full items-center justify-between gap-4">
          <span
            className={`text-xl font-bold ${match.win ? "text-primary" : "text-red-500"}`}
          >
            {match.win ? "W" : "L"}
          </span>
          <div className="flex w-full flex-col gap-1 self-end overflow-x-scroll text-right">
            {match.playerScores
              .sort((a, b) => {
                const customOrder = [1, 3, 2, 4];
                return (
                  customOrder.indexOf(a.order) - customOrder.indexOf(b.order)
                );
              })
              .map((score, j) => (
                <PlayerScoreCard
                  key={j}
                  score={score}
                  matchWin={match.win}
                  teamId={teamId}
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

export function PlayerRating({ rating }: { rating: number }) {
  // circular little SVG

  const bgColor = {
    1: "bg-red-500",
    2: "bg-red-800",
    3: "bg-purple-500",
    4: "bg-purple-800",
    5: "bg-blue-800",
    6: "bg-blue-500",
  };

  return (
    <span
      className={`rounded-full px-3 py-2 font-spacemono text-[0.6rem] not-italic ${bgColor[rating] || "bg-gray-500"} text-white`}
    >
      {rating}
    </span>
  );
}

function PlayerScoreCard({
  score,
  matchWin,
  teamId,
}: {
  score: PlayerScore;
  matchWin: boolean;
  teamId: number;
}) {
  const playerOnTargetTeam = teamId === score.teamId;
  const playerOnCurrentTeam = score.activeOnCurrentTeam;

  return (
    <div
      key={score.playerId}
      className={`flex flex-1 flex-col items-end gap-1 rounded-xl p-2 ${
        matchWin !== playerOnTargetTeam ? "bg-red-800/20" : "bg-green-800/20"
      } ${
        !playerOnTargetTeam || playerOnCurrentTeam ? "" : "line-through"
      } ${teamId !== score.teamId ? "text-[0.7rem] italic" : ""}`}
    >
      <div className="flex items-center gap-2">
        {score.name}
        <PlayerRating rating={score.playerRating} />
      </div>
      {score.score > 1 && (
        <span
          className={`-mt-1 font-spacemono ${
            teamId !== score.teamId ? "text-[0.7rem]" : ""
          }`}
        >
          {approximateScore(score.score)}
        </span>
      )}
    </div>
  );
}
